/**
 * AnnouncementChart: Area chart showing project announcements over time
 * Uses Chart.js for rendering
 */

import { Chart, registerables } from 'chart.js';

// Register all Chart.js components
Chart.register(...registerables);

// Chart color configuration matching tracker theme
const CHART_COLORS = {
    PRIMARY: '#007D41',      // Darker green (CC Projects)
    SECONDARY: '#00DC6E',    // Lighter green (Calcination Kilns)
    COMBINED: '#00AC56',     // Mid green for combined view
    GRID: 'rgba(0, 0, 0, 0.1)',
    TEXT: '#102A40',
    BACKGROUND_PRIMARY: 'rgba(0, 125, 65, 0.3)',
    BACKGROUND_SECONDARY: 'rgba(0, 220, 110, 0.3)',
    BACKGROUND_COMBINED: 'rgba(0, 172, 86, 0.3)',
};

const CONTINENT_ORDER = [
    'Africa',
    'Asia',
    'Europe',
    'North America',
    'South America',
    'Oceania',
    'Unknown',
];

// Match tracker map legend gradient (see tracker-map.js CONFIG.LEGEND_COLORS)
const LEGEND_MIN_COLOR = '#007D41';
const LEGEND_MAX_COLOR = '#00DC6E';
const LEGEND_ZERO_COLOR = '#D0D0D0';

function hexToRgba(hex, alpha) {
    const h = String(hex || '').replace('#', '').trim();
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return `rgba(0,0,0,${alpha})`;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function hexToRgb(hex) {
    const h = String(hex || '').replace('#', '').trim();
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return { r: 0, g: 0, b: 0 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
}

function interpolateRgb(a, b, t) {
    const clampT = Math.max(0, Math.min(1, t));
    return {
        r: Math.round(a.r + (b.r - a.r) * clampT),
        g: Math.round(a.g + (b.g - a.g) * clampT),
        b: Math.round(a.b + (b.b - a.b) * clampT),
    };
}

function rgbToHex({ r, g, b }) {
    const to2 = (n) => n.toString(16).padStart(2, '0');
    return `#${to2(r)}${to2(g)}${to2(b)}`;
}

function generateLegendPalette(count) {
    const n = Math.max(1, count);
    const minRgb = hexToRgb(LEGEND_MIN_COLOR);
    const maxRgb = hexToRgb(LEGEND_MAX_COLOR);
    if (n === 1) return [rgbToHex(interpolateRgb(minRgb, maxRgb, 0.5))];
    return Array.from({ length: n }, (_, i) => {
        const t = i / (n - 1);
        return rgbToHex(interpolateRgb(minRgb, maxRgb, t));
    });
}

export default class AnnouncementChart {
    /**
     * @param {HTMLElement} container - Container element for the chart
     * @param {string} mode - 'steel' or 'cement'
     */
    constructor(container, mode = 'steel') {
        this.container = container;
        this.mode = mode;
        this.chart = null;
        this.viewMode = 'both'; // cement tabs: 'both', 'cc', 'kilns'
        this.allProjects = []; // Store all filtered projects for re-aggregation

        this.init();
    }

    init() {
        // Create wrapper
        this.wrapper = document.createElement('div');
        this.wrapper.className = 'tracker-chart-section';

        // Create header with title and toggle (cement only)
        this.header = document.createElement('div');
        this.header.className = 'tracker-chart-header';

        const title = document.createElement('h3');
        title.className = 'tracker-chart-title';
        title.textContent = 'Announcements Over Time';
        this.header.appendChild(title);

        // Add toggle for cement mode
        if (this.mode === 'cement') {
            this.createToggle();
        }

        this.wrapper.appendChild(this.header);

        // Create canvas container
        this.canvasContainer = document.createElement('div');
        this.canvasContainer.className = 'tracker-chart-canvas-container';

        this.canvas = document.createElement('canvas');
        this.canvas.className = 'tracker-chart-canvas';
        this.canvas.setAttribute('role', 'img');
        this.canvas.setAttribute('aria-label', 'Area chart showing project announcements by year');
        this.canvasContainer.appendChild(this.canvas);
        this.wrapper.appendChild(this.canvasContainer);

        // Create empty state message
        this.emptyState = document.createElement('div');
        this.emptyState.className = 'tracker-chart-empty hidden';
        this.emptyState.textContent = 'No announcement date data available for current filters';
        this.wrapper.appendChild(this.emptyState);

        this.container.appendChild(this.wrapper);

        // Initialize empty chart
        this.createChart();
    }

    createToggle() {
        const toggleContainer = document.createElement('div');
        toggleContainer.className = 'tracker-chart-toggle';

        const options = [
            { value: 'both', label: 'Both' },
            { value: 'cc', label: 'CC Projects' },
            { value: 'kilns', label: 'Calcination Kilns' },
        ];

        options.forEach((opt) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'tracker-chart-toggle-btn';
            btn.dataset.value = opt.value;
            btn.textContent = opt.label;
            if (opt.value === this.viewMode) {
                btn.classList.add('active');
            }
            btn.addEventListener('click', () => this.setViewMode(opt.value));
            toggleContainer.appendChild(btn);
        });

        this.header.appendChild(toggleContainer);
    }

    setViewMode(mode) {
        if (this.viewMode === mode) return;
        this.viewMode = mode;

        // Update toggle button states
        const buttons = this.header.querySelectorAll('.tracker-chart-toggle-btn');
        buttons.forEach((btn) => {
            btn.classList.toggle('active', btn.dataset.value === mode);
        });

        // Re-render chart with current data
        this.updateChartData();
    }

    createChart() {
        const ctx = this.canvas.getContext('2d');

        this.chart = new Chart(ctx, {
            type: 'line',
            data: {
                labels: [],
                datasets: [],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'index',
                    intersect: false,
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'top',
                        labels: {
                            color: CHART_COLORS.TEXT,
                            usePointStyle: true,
                            padding: 16,
                            font: {
                                size: 12,
                            },
                        },
                    },
                    tooltip: {
                        backgroundColor: 'rgba(16, 42, 64, 0.9)',
                        titleColor: '#fff',
                        bodyColor: '#fff',
                        padding: 12,
                        displayColors: true,
                        callbacks: {
                            label: (context) => {
                                const label = context.dataset.label || '';
                                const value = context.parsed.y;
                                // Hide lines with 0 announcements
                                if (value === 0) return null;
                                return `${label}: ${value} announcement${value !== 1 ? 's' : ''}`;
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        stacked: true,
                        grid: {
                            color: CHART_COLORS.GRID,
                        },
                        ticks: {
                            color: CHART_COLORS.TEXT,
                            font: {
                                size: 11,
                            },
                        },
                        title: {
                            display: true,
                            text: 'Year',
                            color: CHART_COLORS.TEXT,
                            font: {
                                size: 12,
                                weight: '500',
                            },
                        },
                    },
                    y: {
                        stacked: true,
                        beginAtZero: true,
                        grid: {
                            color: CHART_COLORS.GRID,
                        },
                        ticks: {
                            color: CHART_COLORS.TEXT,
                            font: {
                                size: 11,
                            },
                            stepSize: 1,
                            callback: (value) => Number.isInteger(value) ? value : '',
                        },
                        title: {
                            display: true,
                            text: 'Announcements',
                            color: CHART_COLORS.TEXT,
                            font: {
                                size: 12,
                                weight: '500',
                            },
                        },
                    },
                },
                animation: {
                    duration: 400,
                },
            },
        });
    }

    /**
     * Update chart with new project data
     * @param {Array} projects - Array of project objects with announcementYear property
     */
    update(projects) {
        this.allProjects = projects || [];
        this.updateChartData();
    }

    updateChartData() {
        if (!this.chart) return;

        const projects = this.allProjects;

        // Filter projects with valid announcement years
        const projectsWithDates = projects.filter((p) => p.announcementYear != null);

        if (projectsWithDates.length === 0) {
            this.showEmptyState();
            return;
        }

        this.hideEmptyState();

        // Aggregate by year based on mode
        let datasets;
        let labels;

        let filtered = projectsWithDates;
        if (this.mode === 'cement') {
            if (this.viewMode === 'cc') filtered = projectsWithDates.filter((p) => (p.typeNormalized || '') === 'cc');
            else if (this.viewMode === 'kilns') filtered = projectsWithDates.filter((p) => (p.typeNormalized || '') === 'kilns');
        }

        const { years, continents, series } = this.aggregateByYearAndContinent(filtered);
        labels = years;
        const visibleContinents = continents.filter((c) => c !== 'Unknown');
        const legendPalette = generateLegendPalette(visibleContinents.length);
        datasets = continents.map((continent, idx) => {
            const color = continent === 'Unknown'
                ? LEGEND_ZERO_COLOR
                : legendPalette[Math.max(0, visibleContinents.indexOf(continent)) % legendPalette.length];
            return {
                label: continent,
                data: series.get(continent) || [],
                borderColor: color,
                backgroundColor: hexToRgba(color, 0.35),
                fill: true,
                tension: 0.3,
                pointRadius: 3,
                pointHoverRadius: 5,
                stack: 'continents',
            };
        });

        // Update chart
        this.chart.data.labels = labels;
        this.chart.data.datasets = datasets;
        this.chart.options.plugins.legend.display = datasets.length > 1;
        this.chart.update('active');
    }

    /**
     * Aggregate projects by year and continent
     */
    aggregateByYearAndContinent(projects) {
        const yearsSet = new Set();
        const counts = new Map(); // continent -> Map(year -> count)

        projects.forEach((p) => {
            const year = p.announcementYear;
            if (year == null) return;
            yearsSet.add(year);
            const continent = String(p.continent || '').trim() || 'Unknown';
            if (!counts.has(continent)) counts.set(continent, new Map());
            const byYear = counts.get(continent);
            byYear.set(year, (byYear.get(year) || 0) + 1);
        });

        const sortedYears = Array.from(yearsSet).sort((a, b) => a - b);
        const sortContinent = (a, b) => {
            const ia = CONTINENT_ORDER.indexOf(a);
            const ib = CONTINENT_ORDER.indexOf(b);
            const ra = ia === -1 ? 999 : ia;
            const rb = ib === -1 ? 999 : ib;
            if (ra !== rb) return ra - rb;
            return String(a).localeCompare(String(b));
        };
        const continents = Array.from(counts.keys()).sort(sortContinent);

        const series = new Map();
        continents.forEach((c) => {
            const byYear = counts.get(c) || new Map();
            series.set(c, sortedYears.map((y) => byYear.get(y) || 0));
        });

        return { years: sortedYears.map(String), continents, series };
    }

    showEmptyState() {
        this.canvasContainer.classList.add('hidden');
        this.emptyState.classList.remove('hidden');
    }

    hideEmptyState() {
        this.canvasContainer.classList.remove('hidden');
        this.emptyState.classList.add('hidden');
    }

    /**
     * Cleanup chart resources
     */
    destroy() {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
        if (this.wrapper && this.wrapper.parentNode) {
            this.wrapper.parentNode.removeChild(this.wrapper);
        }
    }
}
